import type { ILayer, ILine, IRect, IRenderer, IShape } from '@/lib/CanvasEngine/backend/IRenderer'
import type { Vector2d } from '../../../types'
import { SHAPES } from '../../../const'
import type { BaseBalloonExtension } from '../baseBalloonExtension'
/**
 * ===========================================
 * Balloon 스냅 동작 설명 (스마트 가이드 방식)
 * ===========================================
 *
 * 상용 그래픽 툴(Figma, PowerPoint 등)의 스마트 가이드와 동일한 무상태(stateless) 방식.
 *
 * 1. 핵심 원리
 *    - Konva 드래그는 매 이벤트마다 "포인터 기준 위치"로 노드를 재계산하므로,
 *      dragmove 시점의 balloon 위치가 곧 스냅 없는 가상 위치(virtual position)다.
 *    - 매 dragmove마다 가상 위치를 입력으로 스냅을 순수 계산한다.
 *      포인터가 임계값을 벗어나면 별도의 해제 로직 없이 자연스럽게 풀리고,
 *      balloon은 정확히 커서 아래 위치로 복귀한다. (탈출 거리·해제 배율 개념 없음)
 *
 * 2. 임계값은 화면 픽셀 기준
 *    - 모든 진입 거리는 screen px로 정의하고 stage scale로 나눠 월드 좌표로 환산한다.
 *    - zoom 레벨과 무관하게 손끝 감각이 항상 일정하다.
 *
 * 3. 스냅 종류와 우선순위
 *    a) 인접 도킹 스냅 (PROXIMITY): 다른 balloon의 상하좌우 접점 슬롯에
 *       충분히 가까이(PROXIMITY_SNAP_PX) 가져가면 나란히 붙는다.
 *    b) 축 정렬 스냅 (AXIS): X축과 Y축을 독립적으로 검사해
 *       각 축이 다른 balloon 중심(또는 DIRECTION bend point)과 정렬되면 해당 축만 보정한다.
 *       나머지 축은 포인터를 그대로 따라간다.
 *    - 도킹이 더 명시적인 제스처이므로 도킹 > 축 정렬 순으로 판정한다.
 *
 * 4. 경계 떨림 방지 (sticky)
 *    - 직전 프레임에 붙어 있던 후보에 한해 임계값을 STICKY_MULTIPLIER배로 완화한다.
 *    - 임계값 경계에서 마우스 1px 떨림으로 스냅이 깜빡이는 것만 막는 최소한의 상태.
 *
 * 5. 축 정렬 후보 수집
 *    - 현재 뷰포트(여유 포함) 안에 보이는 balloon들만 후보로 사용한다.
 *    - 화면 밖의 보이지 않는 대상에 붙는 혼란을 방지.
 *
 * 6. 시각적 피드백
 *    - 축 정렬: 정렬 축을 관통하는 점선 1줄 + 정렬된 balloon들의 점선 바운더리.
 *    - 도킹: 두 balloon을 잇는 점선 + 대상 balloon의 점선 바운더리.
 *
 * 7. 충돌 방지
 *    - 스냅 결과 위치가 다른 balloon과 겹치면 getCollisionFreePosition으로 밀어낸다.
 */

export enum SnapDirection {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
  BOTH = 'both',
}

export enum SnapType {
  AXIS = 'axis',
  PROXIMITY = 'proximity',
}

// 스냅 상수 — 거리 값은 전부 화면 픽셀(screen px) 기준
export const SNAP_CONSTANTS = {
  // 축 정렬 스냅 진입 거리
  AXIS_SNAP_PX: 6,

  // DIRECTION 도형의 축 정렬 스냅 진입 거리 (더 보수적)
  AXIS_SNAP_PX_DIRECTION: 4,

  // 인접 도킹 스냅 진입 거리
  PROXIMITY_SNAP_PX: 12,

  // 직전 프레임에 붙어 있던 후보의 유지 배율 (경계 떨림 방지용 최소 히스테리시스)
  STICKY_MULTIPLIER: 1.5,

  // 충돌 방지 최소 거리 (balloon 크기 대비)
  COLLISION_MIN_DISTANCE_RATIO: 1.0,

  // 도킹 후보 수집 범위 (balloon 크기 대비)
  PROXIMITY_SEARCH_RANGE_RATIO: 2.5,

  // 축 정렬 후보 수집 시 뷰포트 바깥 여유 (screen px)
  VIEWPORT_MARGIN_PX: 40,
} as const

// 축 정렬 후보 (한 축의 값과 그 값을 만든 원본 좌표)
interface AxisCandidate {
  value: number
  source: Vector2d
  isBendPoint: boolean
}

// 한 프레임의 스냅 평가 결과
interface SnapEvaluation {
  position: Vector2d
  type: SnapType
  direction: SnapDirection
  proximityTarget: Vector2d | null
  snappedX: AxisCandidate | null
  snappedY: AxisCandidate | null
  matchedSourcesX: Vector2d[]
  matchedSourcesY: Vector2d[]
}

/**
 * Balloon 스냅 기능을 관리하는 클래스
 */
export class SnapManager {
  private entity: BaseBalloonExtension

  // 스냅 활성화 여부 (전역 상태)
  private static _isSnappingEnabled: boolean = true

  // 스냅 상태 (직전 평가 결과 — 외부 getter용)
  private snapGuideLines: IShape[] = []
  private isSnapped: boolean = false
  private snapPosition: Vector2d | null = null
  private snapDirection: SnapDirection | null = null

  // 드래그 세션 상태
  private dragStartPointerPos: Vector2d | null = null
  private dragCandidateCenters: Vector2d[] | null = null
  private dragBendPoints: Vector2d[] | null = null

  // sticky 판정용: 직전 프레임에 붙어 있던 후보
  private lastSnapX: number | null = null
  private lastSnapY: number | null = null
  private lastProximityPoint: Vector2d | null = null

  // 가이드라인 스타일 상수
  private readonly GUIDE_LINE_COLOR = '#FF0099'
  private readonly GUIDE_LINE_OPACITY = 0.8
  private readonly GUIDE_LINE_STROKE_WIDTH = 2

  /**
   * SnapManager 생성자
   * @param entity - BaseBalloonExtension 인스턴스
   */
  constructor(entity: BaseBalloonExtension) {
    this.entity = entity
  }

  private get renderer(): IRenderer {
    return this.entity.canvasEngine.getRenderer()
  }

  /**
   * 스냅 활성화 여부 설정 (전역 static)
   * @param enabled - 스냅 활성화 여부
   */
  public static setSnappingEnabled(enabled: boolean): void {
    SnapManager._isSnappingEnabled = enabled
  }

  /**
   * 스냅 활성화 여부 가져오기 (전역 static)
   * @returns 스냅 활성화 여부
   */
  public static getSnappingEnabled(): boolean {
    return SnapManager._isSnappingEnabled
  }

  // 스냅 활성화 여부 확인 (인스턴스 메서드)
  private isSnappingEnabled(): boolean {
    return SnapManager._isSnappingEnabled
  }

  // 스냅 상태 getter
  public getIsSnapped(): boolean {
    return this.isSnapped
  }

  // 스냅 위치 getter
  public getSnapPosition(): Vector2d | null {
    return this.snapPosition
  }

  // 스냅 방향 getter
  public getSnapDirection(): SnapDirection | null {
    return this.snapDirection
  }

  // 드래그 시작 포인터 위치 setter
  public setDragStartPointerPos(pos: Vector2d | null): void {
    this.dragStartPointerPos = pos
  }

  // 드래그 시작 포인터 위치 getter
  public getDragStartPointerPos(): Vector2d | null {
    return this.dragStartPointerPos
  }

  /**
   * 드래그 세션 시작: 스냅 후보(다른 balloon 중심, bend point)를 캐싱한다.
   * 드래그 중 다른 balloon은 움직이지 않으므로 매 프레임 재수집하지 않는다.
   */
  public beginDrag(): void {
    this.resetSnapState()
    this.lastSnapX = null
    this.lastSnapY = null
    this.lastProximityPoint = null
    this.dragCandidateCenters = this.getOtherBalloonCenters()
    this.dragBendPoints =
      this.entity.entityType === SHAPES.DIRECTION ? this.getOtherDirectionBendPoints() : []
  }

  /**
   * 드래그 세션 종료: 캐시와 스냅 상태를 정리한다.
   */
  public endDrag(): void {
    this.dragCandidateCenters = null
    this.dragBendPoints = null
    this.lastSnapX = null
    this.lastSnapY = null
    this.lastProximityPoint = null
    this.resetSnapState()
  }

  /**
   * 매 dragmove마다 호출되는 스냅 평가 (무상태 순수 계산).
   * @param virtualPosition - 포인터를 따라온 balloon의 가상 전역 위치
   * @returns 스냅이 적용된 최종 전역 위치, 스냅 없으면 null
   */
  public evaluateSnap(virtualPosition: Vector2d): Vector2d | null {
    if (!this.isSnappingEnabled()) {
      this.resetSnapState()
      return null
    }

    try {
      const centers = this.dragCandidateCenters ?? this.getOtherBalloonCenters()
      const evaluation = this.computeSnap(virtualPosition, centers)

      if (!evaluation) {
        this.resetSnapState()
        return null
      }

      // 충돌 회피 적용
      const finalPosition = this.getCollisionFreePosition(evaluation.position)

      this.isSnapped = true
      this.snapPosition = finalPosition
      this.snapDirection = evaluation.direction

      this.drawGuides(evaluation, finalPosition)

      return finalPosition
    } catch (error) {
      console.error('스냅 평가 중 에러:', error)
      this.resetSnapState()
      return null
    }
  }

  // 현재 stage의 scale(zoom 레벨) 가져오기 (극단값 방지 클램프 포함)
  private getStageScale(): number {
    const stage = this.renderer.stage
    const scale = stage?.scaleX() || 1
    return Math.max(0.1, Math.min(10, scale))
  }

  // 화면 픽셀 거리를 월드 좌표 거리로 환산
  private toWorldDistance(screenPx: number): number {
    return screenPx / this.getStageScale()
  }

  // 현재 뷰포트 영역(월드 좌표, 여유 포함) 계산
  private getViewportBounds(): { minX: number; minY: number; maxX: number; maxY: number } {
    const stage = this.renderer.stage
    const scale = this.getStageScale()
    const margin = this.toWorldDistance(SNAP_CONSTANTS.VIEWPORT_MARGIN_PX)
    const minX = -stage.x() / scale - margin
    const minY = -stage.y() / scale - margin
    return {
      minX,
      minY,
      maxX: minX + stage.width() / scale + margin * 2,
      maxY: minY + stage.height() / scale + margin * 2,
    }
  }

  /**
   * 스냅 판정 본체: 도킹 스냅 → 축 정렬 스냅 순으로 검사한다.
   */
  private computeSnap(virtualPosition: Vector2d, centers: Vector2d[]): SnapEvaluation | null {
    // 1. 인접 도킹 스냅 (명시적 제스처이므로 우선)
    const proximity = this.findProximitySnap(virtualPosition, centers)
    if (proximity) {
      this.lastProximityPoint = proximity.point
      this.lastSnapX = null
      this.lastSnapY = null
      return {
        position: proximity.point,
        type: SnapType.PROXIMITY,
        direction: proximity.direction,
        proximityTarget: proximity.target,
        snappedX: null,
        snappedY: null,
        matchedSourcesX: [],
        matchedSourcesY: [],
      }
    }
    this.lastProximityPoint = null

    // 2. 축 정렬 스냅 (X/Y 독립 판정)
    const axisPx =
      this.entity.entityType === SHAPES.DIRECTION
        ? SNAP_CONSTANTS.AXIS_SNAP_PX_DIRECTION
        : SNAP_CONSTANTS.AXIS_SNAP_PX
    const threshold = this.toWorldDistance(axisPx)

    const { xCandidates, yCandidates } = this.collectAxisCandidates(centers)
    const snappedX = this.pickAxisCandidate(
      virtualPosition.x,
      xCandidates,
      threshold,
      this.lastSnapX,
    )
    const snappedY = this.pickAxisCandidate(
      virtualPosition.y,
      yCandidates,
      threshold,
      this.lastSnapY,
    )

    this.lastSnapX = snappedX ? snappedX.value : null
    this.lastSnapY = snappedY ? snappedY.value : null

    if (!snappedX && !snappedY) return null

    const direction =
      snappedX && snappedY
        ? SnapDirection.BOTH
        : snappedX
          ? SnapDirection.VERTICAL
          : SnapDirection.HORIZONTAL

    return {
      position: {
        x: snappedX ? snappedX.value : virtualPosition.x,
        y: snappedY ? snappedY.value : virtualPosition.y,
      },
      type: SnapType.AXIS,
      direction,
      proximityTarget: null,
      snappedX,
      snappedY,
      matchedSourcesX: snappedX ? this.collectMatchedSources(xCandidates, snappedX.value) : [],
      matchedSourcesY: snappedY ? this.collectMatchedSources(yCandidates, snappedY.value) : [],
    }
  }

  // 선택된 축 값과 정렬된 balloon 원본 좌표들 수집 (가이드 바운더리 표시용)
  private collectMatchedSources(candidates: AxisCandidate[], value: number): Vector2d[] {
    return candidates
      .filter(c => !c.isBendPoint && Math.abs(c.value - value) < 0.5)
      .map(c => c.source)
  }

  /**
   * 뷰포트 안에 보이는 후보들로 X/Y축 정렬 후보 목록을 만든다.
   */
  private collectAxisCandidates(centers: Vector2d[]): {
    xCandidates: AxisCandidate[]
    yCandidates: AxisCandidate[]
  } {
    const bounds = this.getViewportBounds()
    const xCandidates: AxisCandidate[] = []
    const yCandidates: AxisCandidate[] = []

    for (const center of centers) {
      if (
        center.x < bounds.minX ||
        center.x > bounds.maxX ||
        center.y < bounds.minY ||
        center.y > bounds.maxY
      ) {
        continue
      }
      xCandidates.push({ value: center.x, source: center, isBendPoint: false })
      yCandidates.push({ value: center.y, source: center, isBendPoint: false })
    }

    // DIRECTION 도형: 다른 DIRECTION의 bend point들도 정렬 후보에 포함
    if (
      this.entity.entityType === SHAPES.DIRECTION &&
      !this.entity.isBalloonDragMode &&
      this.entity.getCurrentBendPointCount() < 2 &&
      this.dragBendPoints
    ) {
      for (const bendPoint of this.dragBendPoints) {
        if (
          bendPoint.x < bounds.minX ||
          bendPoint.x > bounds.maxX ||
          bendPoint.y < bounds.minY ||
          bendPoint.y > bounds.maxY
        ) {
          continue
        }
        xCandidates.push({ value: bendPoint.x, source: bendPoint, isBendPoint: true })
        yCandidates.push({ value: bendPoint.y, source: bendPoint, isBendPoint: true })
      }
    }

    return { xCandidates, yCandidates }
  }

  /**
   * 한 축에서 임계값 안의 가장 가까운 후보를 고른다.
   * 직전 프레임에 붙어 있던 값은 STICKY_MULTIPLIER배 완화된 임계값을 적용해 경계 떨림을 막는다.
   */
  private pickAxisCandidate(
    virtualValue: number,
    candidates: AxisCandidate[],
    threshold: number,
    lastValue: number | null,
  ): AxisCandidate | null {
    const stickyThreshold = threshold * SNAP_CONSTANTS.STICKY_MULTIPLIER
    let best: AxisCandidate | null = null
    let bestDistance = Number.MAX_VALUE

    for (const candidate of candidates) {
      const distance = Math.abs(virtualValue - candidate.value)
      const isSticky = lastValue !== null && Math.abs(candidate.value - lastValue) < 0.001
      const limit = isSticky ? stickyThreshold : threshold
      if (distance <= limit && distance < bestDistance) {
        best = candidate
        bestDistance = distance
      }
    }

    return best
  }

  /**
   * 인접 도킹 스냅 대상 찾기: 다른 balloon의 상하좌우 접점 슬롯 중
   * 가상 위치와 충분히 가까운 슬롯을 고른다.
   */
  private findProximitySnap(
    virtualPosition: Vector2d,
    centers: Vector2d[],
  ): { point: Vector2d; target: Vector2d; direction: SnapDirection } | null {
    const balloonSize = this.entity.getBalloonSize()
    const searchRange = balloonSize * SNAP_CONSTANTS.PROXIMITY_SEARCH_RANGE_RATIO
    const threshold = this.toWorldDistance(SNAP_CONSTANTS.PROXIMITY_SNAP_PX)
    const stickyThreshold = threshold * SNAP_CONSTANTS.STICKY_MULTIPLIER

    let best: { point: Vector2d; target: Vector2d; direction: SnapDirection } | null = null
    let bestDistance = Number.MAX_VALUE

    for (const center of centers) {
      const centerDistance = Math.hypot(virtualPosition.x - center.x, virtualPosition.y - center.y)
      if (centerDistance > searchRange) continue

      // 상하좌우 접점 슬롯 (두 balloon이 나란히 맞닿는 위치)
      const slots = [
        { x: center.x + balloonSize, y: center.y, direction: SnapDirection.HORIZONTAL },
        { x: center.x - balloonSize, y: center.y, direction: SnapDirection.HORIZONTAL },
        { x: center.x, y: center.y + balloonSize, direction: SnapDirection.VERTICAL },
        { x: center.x, y: center.y - balloonSize, direction: SnapDirection.VERTICAL },
      ]

      for (const slot of slots) {
        // 슬롯 자리를 이미 다른 balloon이 차지하고 있으면 제외
        const isOccupied = centers.some(other => {
          if (other === center) return false
          return Math.hypot(slot.x - other.x, slot.y - other.y) < balloonSize * 0.9
        })
        if (isOccupied) continue

        const distance = Math.hypot(virtualPosition.x - slot.x, virtualPosition.y - slot.y)
        const isSticky =
          this.lastProximityPoint !== null &&
          Math.abs(slot.x - this.lastProximityPoint.x) < 0.001 &&
          Math.abs(slot.y - this.lastProximityPoint.y) < 0.001
        const limit = isSticky ? stickyThreshold : threshold

        if (distance <= limit && distance < bestDistance) {
          best = { point: { x: slot.x, y: slot.y }, target: center, direction: slot.direction }
          bestDistance = distance
        }
      }
    }

    return best
  }

  /**
   * 다른 DIRECTION shape들의 bend point들을 수집
   * @returns Vector2d[] - 모든 DIRECTION shape의 bend point 좌표
   */
  private getOtherDirectionBendPoints(): Vector2d[] {
    const bendPoints: Vector2d[] = []

    // canvasEngine에서 모든 엔티티 가져오기
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allEntities = (this.entity.canvasEngine as any).entities || []

    for (const entity of allEntities) {
      // 현재 엔티티는 제외하고 DIRECTION 타입 엔티티만 처리
      if (
        entity.getId() !== this.entity.getId() &&
        entity.entityType === SHAPES.DIRECTION &&
        entity.__directionPoints &&
        entity.__directionPoints.length >= 4
      ) {
        const points = entity.__directionPoints

        // 모든 점을 Vector2d 형태로 변환하여 추가
        for (let i = 0; i < points.length; i += 2) {
          if (i + 1 < points.length) {
            bendPoints.push({
              x: points[i],
              y: points[i + 1],
            })
          }
        }
      }
    }

    return bendPoints
  }

  /**
   * 다른 모든 balloon의 전역 중심 좌표를 가져오기
   * @param rangeCenter - 지정 시 이 위치 기준 rangeDistance 안의 balloon만 반환 (충돌 검사 최적화용)
   * @param rangeDistance - 필터링 거리 (월드 좌표)
   */
  private getOtherBalloonCenters(rangeCenter?: Vector2d, rangeDistance?: number): Vector2d[] {
    const balloonCenters: Vector2d[] = []

    // canvasEngine에서 모든 엔티티 가져오기
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allEntities = (this.entity.canvasEngine as any).entities || []

    for (const entity of allEntities) {
      // 현재 드래그 중인 엔티티, brush 타입 엔티티 제외
      if (entity.getId() === this.entity.getId()) continue
      if (entity.isBrush && entity.isBrush()) continue

      // 벌룬이 있는 엔티티만 처리
      if (entity.balloon) {
        // 엔티티의 전역 좌표 계산
        let entityGlobalX: number = 0
        let entityGlobalY: number = 0

        if (entity.isLine && entity.isLine()) {
          // 선의 경우 중앙점 계산
          const points = entity.getPoints()
          entityGlobalX = (points[0] + points[2]) / 2
          entityGlobalY = (points[1] + points[3]) / 2
        } else if (
          entity.entityType === SHAPES.DIRECTION &&
          entity.__directionPoints &&
          entity.__directionPoints.length >= 4
        ) {
          // DIRECTION shape의 경우 balloon이 끝점에 고정되어 있으므로
          // balloon의 상대 좌표가 이미 절대 좌표임
          const balloonPos = entity.balloon.getPosition()
          balloonCenters.push({ x: balloonPos.x, y: balloonPos.y })
          continue
        } else {
          // 일반 도형의 중심 좌표
          entityGlobalX = entity.node.x()
          entityGlobalY = entity.node.y()
        }

        // 벌룬의 상대 좌표
        const balloonPos = entity.balloon.getPosition()

        balloonCenters.push({
          x: entityGlobalX + balloonPos.x,
          y: entityGlobalY + balloonPos.y,
        })
      }
    }

    if (rangeCenter && rangeDistance !== undefined) {
      return balloonCenters.filter(center => {
        return Math.hypot(rangeCenter.x - center.x, rangeCenter.y - center.y) <= rangeDistance
      })
    }

    return balloonCenters
  }

  /**
   * balloon 충돌 감지 함수
   */
  public checkCollisionWithOtherBalloons(targetPosition: Vector2d): boolean {
    const minDistance = this.entity.getBalloonSize() * SNAP_CONSTANTS.COLLISION_MIN_DISTANCE_RATIO
    const otherBalloonCenters = this.getOtherBalloonCenters(targetPosition, minDistance * 2)

    for (const balloonCenter of otherBalloonCenters) {
      const distance = Math.hypot(
        targetPosition.x - balloonCenter.x,
        targetPosition.y - balloonCenter.y,
      )
      if (distance < minDistance) {
        return true
      }
    }

    return false
  }

  /**
   * 충돌을 피하는 허용 가능한 위치 계산
   */
  public getCollisionFreePosition(targetPosition: Vector2d): Vector2d {
    const minDistance = this.entity.getBalloonSize() * SNAP_CONSTANTS.COLLISION_MIN_DISTANCE_RATIO
    const otherBalloonCenters = this.getOtherBalloonCenters(targetPosition, minDistance * 2)
    let adjustedPosition = { ...targetPosition }

    for (const balloonCenter of otherBalloonCenters) {
      const distance = Math.hypot(
        adjustedPosition.x - balloonCenter.x,
        adjustedPosition.y - balloonCenter.y,
      )

      if (distance < minDistance) {
        // 겹침이 발생하면 겹친 balloon 중심에서 바깥 방향으로 밀어냄
        const angle = Math.atan2(
          adjustedPosition.y - balloonCenter.y,
          adjustedPosition.x - balloonCenter.x,
        )
        adjustedPosition = {
          x: balloonCenter.x + Math.cos(angle) * (minDistance + 1),
          y: balloonCenter.y + Math.sin(angle) * (minDistance + 1),
        }
      }
    }
    return adjustedPosition
  }

  // 가이드라인용 Line 생성
  private createGuideLine(config: {
    points: number[]
    isDashed?: boolean
    dashPattern?: number[]
  }): ILine {
    const lineConfig: {
      points: number[]
      stroke: string
      strokeWidth: number
      opacity: number
      dash?: number[]
    } = {
      points: config.points,
      stroke: this.GUIDE_LINE_COLOR,
      strokeWidth: this.GUIDE_LINE_STROKE_WIDTH,
      opacity: this.GUIDE_LINE_OPACITY,
    }

    if (config.isDashed) {
      lineConfig.dash = config.dashPattern || [5, 5]
    }

    return this.renderer.createLine(lineConfig)
  }

  // 바운더리용 점선 Rect 생성
  private createBoundaryRect(config: {
    x: number
    y: number
    width: number
    height: number
  }): IRect {
    return this.renderer.createRect({
      x: config.x,
      y: config.y,
      width: config.width,
      height: config.height,
      stroke: this.GUIDE_LINE_COLOR,
      strokeWidth: this.GUIDE_LINE_STROKE_WIDTH,
      opacity: this.GUIDE_LINE_OPACITY,
      dash: [3, 3],
    })
  }

  // 도킹 스냅용 연결선 Line 생성
  private createConnectionLine(config: { points: number[] }): ILine {
    return this.renderer.createLine({
      points: config.points,
      stroke: this.GUIDE_LINE_COLOR,
      strokeWidth: this.GUIDE_LINE_STROKE_WIDTH,
      opacity: 1,
      dash: [2, 4],
    })
  }

  // balloon 위치에 점선 바운더리 Rect 추가
  private addBoundaryAt(center: Vector2d, systemLayer: ILayer): void {
    const balloonSize = this.entity.getBalloonSize()
    const boundaryRect = this.createBoundaryRect({
      x: center.x - balloonSize / 2,
      y: center.y - balloonSize / 2,
      width: balloonSize,
      height: balloonSize,
    })
    this.snapGuideLines.push(boundaryRect)
    systemLayer.add(boundaryRect)
  }

  /**
   * 스냅 가이드 그리기 (스마트 가이드 스타일)
   * - 축 정렬: 정렬 축 점선 + 정렬된 balloon들의 점선 바운더리
   * - 도킹: 두 balloon을 잇는 점선 + 대상 balloon 바운더리
   */
  private drawGuides(evaluation: SnapEvaluation, finalPosition: Vector2d): void {
    this.clearSnapGuideLines()

    const systemLayer: ILayer | undefined = this.renderer.layers.system
    if (!systemLayer) return

    const stage = this.renderer.stage
    const scale = stage.scaleX() || 1
    const viewportX = -stage.x() / scale
    const viewportY = -stage.y() / scale
    const viewportWidth = stage.width() / scale
    const viewportHeight = stage.height() / scale

    if (evaluation.type === SnapType.PROXIMITY && evaluation.proximityTarget) {
      // 도킹: 두 balloon 중심을 잇는 점선 + 대상/현재 balloon 바운더리
      const connectionLine = this.createConnectionLine({
        points: [
          finalPosition.x,
          finalPosition.y,
          evaluation.proximityTarget.x,
          evaluation.proximityTarget.y,
        ],
      })
      this.snapGuideLines.push(connectionLine)
      systemLayer.add(connectionLine)

      this.addBoundaryAt(evaluation.proximityTarget, systemLayer)
      this.addBoundaryAt(finalPosition, systemLayer)
    } else {
      // 축 정렬: 스냅된 축마다 뷰포트를 관통하는 점선 1줄
      if (evaluation.snappedX) {
        const verticalLine = this.createGuideLine({
          points: [finalPosition.x, viewportY, finalPosition.x, viewportY + viewportHeight],
          isDashed: true,
        })
        this.snapGuideLines.push(verticalLine)
        systemLayer.add(verticalLine)
      }

      if (evaluation.snappedY) {
        const horizontalLine = this.createGuideLine({
          points: [viewportX, finalPosition.y, viewportX + viewportWidth, finalPosition.y],
          isDashed: true,
        })
        this.snapGuideLines.push(horizontalLine)
        systemLayer.add(horizontalLine)
      }

      // 정렬된 balloon들과 현재 balloon에 점선 바운더리 표시 (bend point 스냅은 선만)
      const matchedSources = [...evaluation.matchedSourcesX, ...evaluation.matchedSourcesY]
      if (matchedSources.length > 0) {
        for (const source of matchedSources) {
          this.addBoundaryAt(source, systemLayer)
        }
        this.addBoundaryAt(finalPosition, systemLayer)
      }
    }

    this.snapGuideLines.forEach(line => line.moveToBottom())
    systemLayer.batchDraw()
  }

  /**
   * 유도선 제거
   */
  public clearSnapGuideLines(): void {
    if (this.snapGuideLines.length === 0) return

    for (const line of this.snapGuideLines) {
      line.remove()
    }
    this.snapGuideLines = []

    const systemLayer: ILayer | undefined = this.renderer.layers.system
    if (systemLayer) {
      systemLayer.batchDraw()
    }
  }

  /**
   * 스냅 상태 초기화
   */
  public resetSnapState(): void {
    this.isSnapped = false
    this.snapPosition = null
    this.snapDirection = null

    try {
      this.clearSnapGuideLines()
    } catch (error) {
      console.error('가이드라인 정리 중 에러:', error)
    }
  }
}
